import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory, Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Row,
  Col,
  CardTitle,
  Button,
  Table,
  Input,
  FormGroup,
  Spinner,
  InputGroup,
  InputGroupText,
  Tooltip,
} from "reactstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown, faCalendarAlt, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Select from "react-select";
import ReactDatetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import footballService from "../../services/api/football-service.js";


const SortableHeader = ({ children, columnKey, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === columnKey;
  let icon = faSort;
  if (isActive) {
    icon = sortOrder === 'asc' ? faSortUp : faSortDown;
  }
  return (
    <div
      className="d-flex align-items-center cursor-pointer select-none text-nowrap user-select-none"
      onClick={() => onSort(columnKey)}
    >
      <span className="me-2">{children}</span>
      <FontAwesomeIcon icon={icon} className="text-muted" />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    completed: "bg-light text-dark",
    not_started: "bg-secondary text-white",
    live: "bg-success text-white",
  };
  const defaultStyle = "bg-warning text-dark";
  return (
    <span className={`badge rounded-pill fw-bold ${styles[status] || defaultStyle}`}>
      {status?.replace(/_/g, " ").toUpperCase() || "N/A"}
    </span>
  );
};

const CustomPagination = ({ activePage, itemsCountPerPage, totalItemsCount, pageRangeDisplayed, onChange }) => {
  const pageNumbers = [];
  const totalPages = Math.ceil(totalItemsCount / itemsCountPerPage);
  const startPage = Math.max(1, activePage - Math.floor(pageRangeDisplayed / 2));
  const endPage = Math.min(totalPages, startPage + pageRangeDisplayed - 1);

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <ul className="pagination pagination-sm justify-content-end m-0">
      <li className={`page-item ${activePage === 1 ? 'disabled' : ''}`}>
        <button className="page-link" onClick={() => onChange(activePage - 1)}>&laquo;</button>
      </li>
      {pageNumbers.map(number => (
        <li key={number} className={`page-item ${activePage === number ? 'active' : ''}`}>
          <button className="page-link" onClick={() => onChange(number)}>{number}</button>
        </li>
      ))}
      <li className={`page-item ${activePage === totalPages ? 'disabled' : ''}`}>
        <button className="page-link" onClick={() => onChange(activePage + 1)}>&raquo;</button>
      </li>
    </ul>
  );
};

function MatchesPage({ refetchTrigger, tslug: tslugFromProps, roundSlug: roundSlugFromProps }) {
  const history = useHistory();
  const queryParams = useMemo(() => new URLSearchParams(history.location.search), [history.location.search]);

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatches, setTotalMatches] = useState(0);
  const [pageSize] = useState(10);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [jumpToPage, setJumpToPage] = useState('');
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const toggleTooltip = (id) => setActiveTooltipId(id === activeTooltipId ? null : id);

  const initialFilters = { query: "", status: "", tslug: "", roundSlug: "", fromDate: null, toDate: null };
  const [filters, setFilters] = useState(initialFilters);

  const [allTournaments, setAllTournaments] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [roundOptions, setRoundOptions] = useState([]);

  const records = pageSize;

  const fetchMatchesData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Prepare params for the service
    const params = {
      page: currentPage,
      size: pageSize,
      sortBy,
      sortOrder,
    };
    if (filters.query) params.q = filters.query.trim();
    if (filters.status) params.status = filters.status;
    if (filters.tslug) params.tslug = filters.tslug;
    if (filters.roundSlug) params.roundSlug = filters.roundSlug;
    if (filters.fromDate) params.fromDate = moment.utc(filters.fromDate).unix();
    if (filters.toDate) params.toDate = moment.utc(filters.toDate).unix();

    try {
      const data = await footballService.getMatches(params);
      setMatches(data.data);
      setTotalPages(data.totalPages);
      setTotalMatches(data.total);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to fetch matches";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, sortBy, sortOrder, filters]);

  useEffect(() => {
    setFilters(prev => ({ ...initialFilters, tslug: tslugFromProps || '', roundSlug: roundSlugFromProps || '' }));
    setCurrentPage(1);
  }, [tslugFromProps, roundSlugFromProps]);

  useEffect(() => {
    const debounceTimeout = setTimeout(fetchMatchesData, 500);
    return () => clearTimeout(debounceTimeout);
  }, [fetchMatchesData]);

  useEffect(() => {
    if (refetchTrigger > 0) {
      fetchMatchesData();
    }
  }, [refetchTrigger]);

  useEffect(() => {
    const fetchDataForFilters = async () => {
      try {
        const [tournamentsData, roundsData, statusesData] = await Promise.all([
          footballService.getTournaments({ size: 1000 }),
          footballService.getAllRounds(),
          footballService.getMatchStatuses()
        ]);

        if (tournamentsData.status === 'success') setAllTournaments(tournamentsData.data);
        if (roundsData.data) {
          setAllRounds(roundsData.data);
          setRoundOptions(roundsData.data);
        }
        if (statusesData.data) setStatuses(statusesData.data);

      } catch (err) {
        toast.error("Failed to fetch filter options.");
      }
    };
    fetchDataForFilters();
  }, []);

  // CHANGED: Logic updated to use t_slug and r_slug
  useEffect(() => {
    if (!filters.tslug) {
      setRoundOptions(allRounds); // Show all rounds if no tournament is selected
      return;
    }
    const selectedTournament = allTournaments.find(t => t.t_slug === filters.tslug);
    if (selectedTournament && selectedTournament.rounds) {
      const roundSlugsForTournament = selectedTournament.rounds.map(r => r.r_slug);
      const filteredRounds = allRounds.filter(r => roundSlugsForTournament.includes(r.r_slug));
      setRoundOptions(filteredRounds);
    } else {
      setRoundOptions([]);
    }
  }, [filters.tslug, allTournaments, allRounds]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      if (field === 'tslug') {
        newFilters.roundSlug = ""; // Reset round when tournament changes
      }
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  const handleSort = (columnKey) => {
    setSortBy(columnKey);
    setSortOrder((prevOrder) => (sortBy === columnKey && prevOrder === "desc" ? "asc" : "desc"));
    setCurrentPage(1);
  };

 const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString("en-IN", {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: "Asia/Kolkata" // Forces IST
    });
  };

 const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp * 1000).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,           // Converts 24h -> 12h (AM/PM)
      timeZone: "Asia/Kolkata" // Converts UTC -> IST
    });
  };

  const handlePageJump = (e) => {
    if (e.key === 'Enter') {
      const page = parseInt(jumpToPage, 10);
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        setJumpToPage('');
      } else {
        alert(`Please enter a number between 1 and ${totalPages}.`);
        setJumpToPage('');
      }
    }
  };

  const paginationData = useMemo(() => {
    const firstRecord = (currentPage - 1) * records + 1;
    const lastRecord = Math.min(currentPage * records, totalMatches || 0);
    return { firstRecord, lastRecord };
  }, [currentPage, totalMatches, records]);

  return (
    <>
      <div className="p-4 border-bottom border-light">
        <CardTitle tag="h4" className="mb-3 fw-bold">Filter Matches</CardTitle>
        <Row className="g-2">
          <Col lg={4} md={6}> <FormGroup> <Input bsSize="sm" type="search" placeholder="Search by name..." value={filters.query} onChange={(e) => handleFilterChange('query', e.target.value)} /> </FormGroup> </Col>
          <Col lg={4} md={6}> <FormGroup> <Select placeholder="Filter by Tournament" options={allTournaments.map(t => ({ label: t.name, value: t.t_slug }))} value={allTournaments.map(t => ({ label: t.name, value: t.t_slug })).find(o => o.value === filters.tslug) || null} onChange={(selected) => handleFilterChange('tslug', selected ? selected.value : '')} isClearable styles={{ control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '31px' }), menu: (base) => ({ ...base, zIndex: 10 }) }} /> </FormGroup> </Col>
          <Col lg={4} md={6}> <FormGroup> <Select placeholder="Filter by Round" options={roundOptions.map(r => ({ label: r.name, value: r.r_slug }))} value={roundOptions.map(r => ({ label: r.name, value: r.r_slug })).find(o => o.value === filters.roundSlug) || null} onChange={(selected) => handleFilterChange('roundSlug', selected ? selected.value : '')} isClearable styles={{ control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '31px' }), menu: (base) => ({ ...base, zIndex: 9 }) }} /> </FormGroup> </Col>
          <Col lg={4} md={6}> <FormGroup> <Select placeholder="Filter by Status" options={statuses.map((status) => ({ label: status.replace(/_/g, " ").toUpperCase(), value: status, }))} value={statuses.map((status) => ({ label: status.replace(/_/g, " ").toUpperCase(), value: status, })).find(o => o.value === filters.status) || null} onChange={(selected) => handleFilterChange('status', selected ? selected.value : '')} isClearable styles={{ control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '31px' }), menu: (base) => ({ ...base, zIndex: 8 }) }} /> </FormGroup> </Col>
          <Col lg={4} md={6}> <FormGroup> <InputGroup size="sm"> <InputGroupText><FontAwesomeIcon icon={faCalendarAlt} /></InputGroupText>
            <ReactDatetime value={filters.fromDate} onChange={(date) => handleFilterChange("fromDate", date)} timeFormat={true} inputProps={{ placeholder: "From Date & Time", className: "form-control" }} />
          </InputGroup> </FormGroup> </Col>

          <Col lg={4} md={6}> <FormGroup> <InputGroup size="sm"> <InputGroupText><FontAwesomeIcon icon={faCalendarAlt} /></InputGroupText>
            {/* CHANGED: timeFormat is now true */}
            <ReactDatetime value={filters.toDate} onChange={(date) => handleFilterChange("toDate", date)} timeFormat={true} inputProps={{ placeholder: "To Date & Time", className: "form-control" }} />
          </InputGroup> </FormGroup> </Col></Row>
        <Row> <Col> <Button color="warning" size="sm" type="button" onClick={handleFilterReset}>Reset Filters</Button> </Col> </Row>
      </div>

      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center px-4 py-3 border-bottom">
        <div className="text-muted fw-bold small text-nowrap me-2 mb-2 mb-sm-0">
          {totalMatches > 0 ? (`Showing ${paginationData.firstRecord} - ${paginationData.lastRecord} of ${totalMatches} matches`) : ('No matches to display')}
        </div>
        <div className="d-flex align-items-center justify-content-end w-100">
          <CustomPagination activePage={currentPage} itemsCountPerPage={records} totalItemsCount={totalMatches} pageRangeDisplayed={3} onChange={setCurrentPage} />
          <Input type="number" min="1" max={totalPages} placeholder="Go to page" value={jumpToPage} onChange={(e) => setJumpToPage(e.target.value)} onKeyDown={handlePageJump} className="form-control form-control-sm ms-2" style={{ width: '110px' }} />
        </div>
      </div>

      <div className="table-responsive">
        <Table className="table-hover table-striped mb-0">
          <thead className="text-dark bg-light text-uppercase small">
            <tr>
              <th className="text-left py-3 px-4">S.No</th>
              <th className="text-left py-3 px-4"><SortableHeader columnKey="title" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Match</SortableHeader></th>
              <th className="text-center py-3 px-4 d-none d-md-table-cell">Score</th>
              <th className="text-left py-3 px-4">Tournament</th>
              <th className="text-center py-3 px-4"><SortableHeader columnKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Status</SortableHeader></th>
              <th className="text-left py-3 px-4 d-none d-sm-table-cell"><SortableHeader columnKey="date" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Date & Time</SortableHeader></th>
              <th className="text-center py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center py-5"><Spinner animation="border" role="status" className="text-success" /><p className="mt-2 text-muted">Loading matches...</p></td></tr>
            ) : error ? (
              <tr><td colSpan="7" className="text-center text-danger py-5">{error}</td></tr>
            ) : matches.length > 0 ? (
              matches.map(({ match, score }, index) => (
                <tr key={match.m_slug} className="align-middle">
                  <td className="text-left px-4">{(currentPage - 1) * records + index + 1}</td>
                  <td className="text-left px-4 fw-bold">{match.title}</td>
                  <td className="text-center px-4 d-none d-md-table-cell"><span className="fw-bold text-success fs-5">{score ? `${score.home} - ${score.away}` : 'vs'}</span></td>
                  <td className="text-left px-4"><Link className="text-dark text-decoration-none" to={`/dashboard/football/tournament/${match.tournament.t_slug}`}>{match.tournament.name}</Link></td>
                  <td className="text-center px-4"><StatusBadge status={match.status} /></td>
                  <td className="text-left px-4 d-none d-sm-table-cell">{formatDate(match.start?.timestamp)}<br className="d-block d-md-none" /><span className="text-muted small ms-1 d-none d-sm-inline-block">{formatTime(match.start?.timestamp)}</span></td>
                  <td className="text-center px-4">
  <div className="d-flex align-items-center justify-content-center">
    <Link to={`/dashboard/football/match/${match.m_slug}`}>
      <Button
        color="success"
        size="sm"
        className="btn-icon-only rounded-circle"
        id={`detailsBtn_${match.m_slug}`}
        style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}
      >
        <FontAwesomeIcon icon={faArrowRight} />
      </Button>
    </Link>
    <Tooltip
      placement="top"
      isOpen={activeTooltipId === `detailsBtn_${match.m_slug}`}
      target={`detailsBtn_${match.m_slug}`}
      toggle={() => toggleTooltip(`detailsBtn_${match.m_slug}`)}
    >
      Go to Details
    </Tooltip>
  </div>
</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="text-center py-5"><h4 className="text-muted">No Matches Found</h4><p className="text-muted">Try a different search or filter.</p></td></tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default MatchesPage;